import {
    Component,
    computed,
    effect,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { ProgressSpinner } from 'primeng/progressspinner';
import { SelectButton } from 'primeng/selectbutton';
import { Textarea } from 'primeng/textarea';
import {
    CategoryType,
    GameTemplate,
    SiteTemplate,
    WinMode,
} from '@/lib-backend';
import { SiteTemplateService } from '@/app/services/site-template.service';
import { ProductAssetService } from '@/app/services/product-asset.service';
import { SharedContentService } from '@/app/services/shared-content.service';
import { NotificationService } from '@/app/services/notification.service';
import { AuthService } from '@/app/services/auth.service';
import { BrandKit, BrandKitService } from '@/app/services/brand-kit.service';
import {
    AiAssistApiError,
    AiAssistService,
    BaseArtCandidate,
    BaseArtResult,
    GenerateMode,
    VENDOR_FLUX,
} from '@/app/services/ai-assist.service';
import { GamePlateType } from '@/app/components/ai-base-art-generate';
import { ImagePreviewService } from '@/app/services/image-preview.service';
import { hashPathToken } from '@/app/utils/shared-content-url';

const PLATES: GamePlateType[] = ['icon', 'banner'];
const SAMPLES = 2;

const CATEGORY_LABELS: Partial<Record<CategoryType, string>> = {
    [CategoryType.SkillBasedGames]: 'Skill-based games',
    [CategoryType.LuckyBasedGames]: 'Lucky-based games',
};

/** One compatible site template the operator can include in the bulk run. */
interface BulkTarget {
    siteTemplateId: string;
    name: string;
    brandKit: BrandKit | null;
    hasIcon: boolean;
    hasBanner: boolean;
    include: boolean;
    /** Null when generatable, otherwise why this template can't be included. */
    blockedReason: string | null;
}

type CellStatus = 'generating' | 'done' | 'error';

/** One (site template, plate) generation in the results grid. */
interface BulkCell {
    key: string;
    siteTemplateId: string;
    siteName: string;
    asset: GamePlateType;
    status: CellStatus;
    candidates: BaseArtCandidate[];
    /** AI-bucket URL of the candidate currently saved as this slot, if any. */
    selectedUrl: string | null;
    saving: boolean;
    error: string | null;
}

/** Generation parameters for one cell run (shared run, or a per-cell redo). */
interface GenArgs {
    samples: number;
    mode: GenerateMode;
    prompt: string | null;
    operatorNote: string | null;
}

/**
 * Bulk base-art generator: produces `icon` + `banner` plates for a game template
 * across every compatible site template at once, skipping slots that already
 * have content. Results are grouped by site template; the operator picks one
 * candidate per cell (saved immediately) or regenerates a single cell. Mirrors
 * the single-template AI flow ({@link AiBaseArtGenerate}) at fleet scale.
 */
@Component({
    selector: 'app-ai-base-art-bulk',
    standalone: true,
    imports: [
        FormsModule,
        NgClass,
        Button,
        Checkbox,
        Dialog,
        Message,
        ProgressSpinner,
        SelectButton,
        Textarea,
    ],
    templateUrl: './ai-base-art-bulk.html',
})
export class AiBaseArtBulk {
    readonly visible = input.required<boolean>();
    readonly template = input<GameTemplate | null>(null);

    readonly visibleChange = output<boolean>();
    /** Emitted after any save so the host can reload affected base art. */
    readonly changed = output<void>();

    private readonly siteTemplateService = inject(SiteTemplateService);
    private readonly productAsset = inject(ProductAssetService);
    private readonly sharedContent = inject(SharedContentService);
    private readonly notification = inject(NotificationService);
    private readonly auth = inject(AuthService);
    private readonly brandKitService = inject(BrandKitService);
    private readonly aiAssist = inject(AiAssistService);
    protected readonly preview = inject(ImagePreviewService);

    protected readonly phase = signal<'config' | 'results'>('config');
    protected readonly preparing = signal(false);
    protected readonly targets = signal<BulkTarget[]>([]);
    protected readonly cells = signal<BulkCell[]>([]);
    /**
     * Optional operator detail folded into every generation (semi-auto): empty
     * = pure auto; with text the service folds it into the Grok-authored prompt
     * as `operator_note`. Applies to every selected template's plates.
     */
    protected readonly prompt = signal('');

    // ── Per-cell regenerate options dialog ───────────────────────────────────
    protected readonly regenVisible = signal(false);
    private readonly regenTargetKey = signal<string | null>(null);
    protected readonly regenSamples = signal(SAMPLES);
    protected readonly regenPrompt = signal('');
    /** When on, the prompt REPLACES authoring (full-manual); else semi-auto. */
    protected readonly regenFullManual = signal(false);
    protected readonly sampleOptions = [1, 2, 3, 4].map((n) => ({
        label: `${n}`,
        value: n,
    }));

    /** Full-manual needs a prompt; semi-auto / re-roll are always valid. */
    protected readonly canRegen = computed(
        () => !this.regenFullManual() || this.regenPrompt().trim().length > 0,
    );

    private abort: AbortController | null = null;

    protected readonly categoryLabel = computed(() => {
        const cat = this.compatibleCategory();
        return cat ? (CATEGORY_LABELS[cat] ?? cat) : '';
    });

    /** WinMode ↔ CategoryType bridge; null when the game's win mode is unresolved. */
    private readonly compatibleCategory = computed<CategoryType | null>(() => {
        switch (this.template()?.GameKind?.WinMode) {
            case WinMode.SkillBased:
                return CategoryType.SkillBasedGames;
            case WinMode.ChanceBased:
                return CategoryType.LuckyBasedGames;
            default:
                return null;
        }
    });

    protected readonly canManage = computed(() => this.auth.isGlobalAdmin());

    /** Templates the operator has ticked AND that can actually generate. */
    protected readonly includedTargets = computed(() =>
        this.targets().filter((t) => t.include && !t.blockedReason),
    );

    /** Number of (template, plate) cells the run would produce (missing only). */
    protected readonly plannedCellCount = computed(() =>
        this.includedTargets().reduce(
            (n, t) =>
                n + (t.hasIcon ? 0 : 1) + (t.hasBanner ? 0 : 1),
            0,
        ),
    );

    protected readonly creditBlock = computed<string | null>(() =>
        this.aiAssist.blockedVendors().includes(VENDOR_FLUX)
            ? 'Generation paused — FLUX is out of credits. Top up the vendor account to continue.'
            : null,
    );

    protected readonly canGenerate = computed(
        () =>
            this.canManage() &&
            !this.preparing() &&
            this.plannedCellCount() > 0 &&
            !this.creditBlock(),
    );

    /** Cells grouped by site template, for the results grid. */
    protected readonly cellGroups = computed(() => {
        const bySite = new Map<string, { name: string; cells: BulkCell[] }>();
        for (const c of this.cells()) {
            const g = bySite.get(c.siteTemplateId) ?? {
                name: c.siteName,
                cells: [],
            };
            g.cells.push(c);
            bySite.set(c.siteTemplateId, g);
        }
        return [...bySite.values()].sort((a, b) =>
            a.name.localeCompare(b.name),
        );
    });

    constructor() {
        effect(() => {
            if (this.visible()) {
                void this.prepare();
                if (this.aiAssist.status() === 'unknown') {
                    void this.aiAssist.refreshHealth();
                }
                void this.aiAssist.refreshVendorBalances();
            } else {
                this.cancelPolling();
            }
        });
    }

    // ── Config: discover compatible templates ────────────────────────────────

    private async prepare(): Promise<void> {
        this.phase.set('config');
        this.cells.set([]);
        this.targets.set([]);
        const template = this.template();
        const wanted = this.compatibleCategory();
        if (!template || wanted === null) return;

        this.preparing.set(true);
        try {
            if (this.siteTemplateService.templates().length === 0) {
                await this.siteTemplateService.loadTemplates();
            }
            const compatible = this.siteTemplateService
                .templates()
                .filter((st: SiteTemplate) => st.CategoryType === wanted)
                .slice()
                .sort((a, b) => a.Name.localeCompare(b.Name));

            const targets = await Promise.all(
                compatible.map(async (st) => {
                    const [brandKit, art] = await Promise.all([
                        this.brandKitService.resolve(st.SiteTemplateId),
                        this.productAsset.getGameTemplateBaseArt(
                            st.SiteTemplateId,
                            template.Id,
                        ),
                    ]);
                    const props = art?.Properties ?? [];
                    const has = (name: string) =>
                        props.some(
                            (p) =>
                                p.Name.trim().toLowerCase() === name &&
                                !!p.Value,
                        );
                    const hasIcon = has('icon');
                    const hasBanner = has('banner');
                    const noRefs =
                        !brandKit ||
                        brandKit.references.length === 0 ||
                        !brandKit.version;
                    const blockedReason = noRefs
                        ? 'No brand references for AI generation.'
                        : null;
                    const target: BulkTarget = {
                        siteTemplateId: st.SiteTemplateId,
                        name: st.Name,
                        brandKit,
                        hasIcon,
                        hasBanner,
                        // Default-include templates that can generate and still
                        // miss at least one plate (skip fully-populated ones).
                        include: !blockedReason && !(hasIcon && hasBanner),
                        blockedReason,
                    };
                    return target;
                }),
            );
            if (this.visible()) this.targets.set(targets);
        } finally {
            this.preparing.set(false);
        }
    }

    protected toggleInclude(siteTemplateId: string, include: boolean): void {
        this.targets.update((list) =>
            list.map((t) =>
                t.siteTemplateId === siteTemplateId ? { ...t, include } : t,
            ),
        );
    }

    // ── Generate ─────────────────────────────────────────────────────────────

    protected generate(): void {
        if (!this.canGenerate()) return;
        this.cancelPolling();
        this.abort = new AbortController();
        const signal = this.abort.signal;

        const cells: BulkCell[] = [];
        for (const t of this.includedTargets()) {
            for (const asset of PLATES) {
                const exists = asset === 'icon' ? t.hasIcon : t.hasBanner;
                if (exists) continue;
                cells.push({
                    key: `${t.siteTemplateId}:${asset}`,
                    siteTemplateId: t.siteTemplateId,
                    siteName: t.name,
                    asset,
                    status: 'generating',
                    candidates: [],
                    selectedUrl: null,
                    saving: false,
                    error: null,
                });
            }
        }
        this.cells.set(cells);
        this.phase.set('results');

        const opts: GenArgs = {
            samples: SAMPLES,
            ...this.promptArgs(this.prompt(), false),
        };
        for (const cell of cells) {
            const target = this.targets().find(
                (t) => t.siteTemplateId === cell.siteTemplateId,
            );
            if (target)
                void this.runCell(cell.key, target, cell.asset, signal, opts);
        }
    }

    /** Map a (text, fullManual) pair to the request's prompt/mode fields. */
    private promptArgs(
        text: string,
        fullManual: boolean,
    ): { mode: GenerateMode; prompt: string | null; operatorNote: string | null } {
        const t = text.trim();
        if (fullManual) return { mode: 'manual', prompt: t, operatorNote: null };
        if (t) return { mode: 'auto', prompt: null, operatorNote: t };
        return { mode: 'auto', prompt: null, operatorNote: null };
    }

    private async runCell(
        key: string,
        target: BulkTarget,
        asset: GamePlateType,
        signal: AbortSignal,
        opts: GenArgs,
    ): Promise<void> {
        const template = this.template();
        const kit = target.brandKit;
        if (!template || !kit) return;
        this.patchCell(key, { status: 'generating', error: null });
        try {
            const accepted = await this.aiAssist.generateBaseArt({
                jobId: crypto.randomUUID(),
                templateId: target.siteTemplateId,
                version: kit.version ?? '',
                referenceImagesB64: kit.references.map((r) => r.b64),
                assetType: asset,
                styleDescriptor: kit.style,
                gameTemplateId: template.Id,
                gameKind: template.GameKind?.Name ?? template.GameKindId,
                gameMeta: {
                    name: template.Title,
                    description: template.Description,
                },
                mode: opts.mode,
                prompt: opts.prompt,
                operatorNote: opts.operatorNote,
                samples: opts.samples,
            });
            const done = await this.aiAssist.awaitJob(accepted.job_id, {
                signal,
            });
            const result = done.result as BaseArtResult | null;
            this.patchCell(key, {
                status: 'done',
                candidates: result?.candidates ?? [],
            });
        } catch (err) {
            if (signal.aborted) return;
            this.patchCell(key, {
                status: 'error',
                error: this.friendlyError(err),
            });
        }
    }

    /** Open the per-cell regenerate dialog (samples + operator prompt). */
    protected openRegen(key: string): void {
        if (!this.cells().some((c) => c.key === key)) return;
        this.regenTargetKey.set(key);
        this.regenSamples.set(SAMPLES);
        // Seed with the bulk run's operator detail (semi-auto) so a single-cell
        // redo reuses the same guidance by default; the operator can still edit it.
        this.regenPrompt.set(this.prompt());
        this.regenFullManual.set(false);
        this.regenVisible.set(true);
    }

    protected confirmRegen(): void {
        const key = this.regenTargetKey();
        const cell = this.cells().find((c) => c.key === key);
        const target = this.targets().find(
            (t) => t.siteTemplateId === cell?.siteTemplateId,
        );
        if (!cell || !target || !this.canRegen()) return;
        if (!this.abort) this.abort = new AbortController();
        const opts: GenArgs = {
            samples: this.regenSamples(),
            ...this.promptArgs(this.regenPrompt(), this.regenFullManual()),
        };
        this.regenVisible.set(false);
        void this.runCell(cell.key, target, cell.asset, this.abort.signal, opts);
    }

    // ── Select / save ────────────────────────────────────────────────────────

    protected isSelected(key: string, url: string): boolean {
        return this.cells().find((c) => c.key === key)?.selectedUrl === url;
    }

    protected async pick(key: string, candidate: BaseArtCandidate): Promise<void> {
        const cell = this.cells().find((c) => c.key === key);
        const template = this.template();
        if (!cell || !template || cell.saving) return;
        if (cell.selectedUrl === candidate.url) {
            await this.clear(key);
            return;
        }
        this.patchCell(key, { saving: true });
        const path = await this.slotStoragePath(
            template.Id,
            cell.siteTemplateId,
            cell.asset,
        );
        const rehosted = path
            ? await this.sharedContent.rehostImageUrl(path, candidate.url)
            : null;
        if (!rehosted) {
            this.patchCell(key, { saving: false });
            return;
        }
        const { result } = await this.productAsset.updateGameTemplateBaseArt(
            cell.siteTemplateId,
            template.Id,
            [{ Name: cell.asset, Value: rehosted }],
            [],
        );
        if (result === 'Ok') {
            this.patchCell(key, { selectedUrl: candidate.url, saving: false });
            this.markStored(cell.siteTemplateId, cell.asset, true);
            this.changed.emit();
            this.notification.show({
                severity: 'success',
                summary: `Saved ${cell.asset} for ${cell.siteName}`,
            });
        } else {
            this.patchCell(key, { saving: false });
            this.notification.show({
                severity: 'error',
                summary: 'Failed to save base art',
            });
        }
    }

    protected async clear(key: string): Promise<void> {
        const cell = this.cells().find((c) => c.key === key);
        const template = this.template();
        if (!cell || !template || cell.saving || !cell.selectedUrl) return;
        this.patchCell(key, { saving: true });
        const { result } = await this.productAsset.updateGameTemplateBaseArt(
            cell.siteTemplateId,
            template.Id,
            [],
            [cell.asset],
        );
        if (result === 'Ok') {
            this.patchCell(key, { selectedUrl: null, saving: false });
            this.markStored(cell.siteTemplateId, cell.asset, false);
            this.changed.emit();
        } else {
            this.patchCell(key, { saving: false });
            this.notification.show({
                severity: 'error',
                summary: 'Failed to clear base art',
            });
        }
    }

    // ── Dialog lifecycle ─────────────────────────────────────────────────────

    protected backToConfig(): void {
        this.cancelPolling();
        this.phase.set('config');
    }

    protected onVisibleChange(value: boolean): void {
        if (!value) {
            this.cancelPolling();
            // Drop the operator detail so the next open starts blank rather than
            // carrying over the previous run's prompt.
            this.prompt.set('');
        }
        this.visibleChange.emit(value);
    }

    protected close(): void {
        this.onVisibleChange(false);
    }

    private cancelPolling(): void {
        this.abort?.abort();
        this.abort = null;
    }

    private patchCell(key: string, patch: Partial<BulkCell>): void {
        this.cells.update((list) =>
            list.map((c) => (c.key === key ? { ...c, ...patch } : c)),
        );
    }

    private markStored(
        siteTemplateId: string,
        asset: GamePlateType,
        stored: boolean,
    ): void {
        this.targets.update((list) =>
            list.map((t) =>
                t.siteTemplateId === siteTemplateId
                    ? {
                          ...t,
                          hasIcon: asset === 'icon' ? stored : t.hasIcon,
                          hasBanner:
                              asset === 'banner' ? stored : t.hasBanner,
                      }
                    : t,
            ),
        );
    }

    private async slotStoragePath(
        gameTemplateId: string,
        siteTemplateId: string,
        asset: GamePlateType,
    ): Promise<string | null> {
        const hashed = await hashPathToken(siteTemplateId);
        return `products/${gameTemplateId}/${hashed}/${asset}-base`;
    }

    private friendlyError(err: unknown): string {
        if (!(err instanceof AiAssistApiError)) {
            return 'Could not reach the AI Assist service.';
        }
        switch (err.code) {
            case 'vendor_timeout':
                return 'Generation took too long.';
            case 'vendor_unavailable':
                return 'The image model is temporarily unavailable.';
            case 'rate_limited':
                return 'Too many requests right now.';
            case 'content_moderated':
                return 'Blocked by the content filter.';
            case 'invalid_references':
                return 'Reference images could not be used.';
            default:
                return err.message ?? 'Something went wrong.';
        }
    }
}
